import type * as Y from 'yjs';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { IndexeddbPersistence } from 'y-indexeddb';

/** Yjs Awareness type, derived from the provider so we don't take a direct
 *  dependency on `y-protocols` (it's transitive via @hocuspocus/provider). */
export type Awareness = NonNullable<HocuspocusProvider['awareness']>;

export interface SyncOpts { wsUrl: string; room: string; token: string; }

/** Connection state, mirroring Hocuspocus `WebSocketStatus`. */
export type SyncStatus = 'connecting' | 'connected' | 'disconnected';

export interface SyncHandle {
  /** Resolves once both the local (indexeddb) and remote (provider) initial
   *  syncs have settled. */
  whenSynced: Promise<void>;
  destroy: () => void;
  /** Yjs Awareness for presence (cursors/selection/facepile). `null` in the
   *  IndexedDB-only / no-sync path (single-user → no presence). */
  awareness: Awareness | null;
  /** Subscribe to connection-status changes. Replays the current status
   *  immediately, then fires on every change. Returns an unsubscribe fn. */
  onStatus: (cb: (s: SyncStatus) => void) => () => void;
}

/** What a provider factory must expose. */
export interface ProviderHandle {
  destroy: () => void;
  awareness: Awareness | null;
  onStatus: (cb: (s: SyncStatus) => void) => () => void;
}

/** Injection seam so tests can supply fakes instead of real WS/IndexedDB. */
export interface SyncFactories {
  makeProvider: (cfg: { url: string; name: string; token: string; document: Y.Doc; onSynced: () => void }) => ProviderHandle;
  makeIndexeddb: (room: string, doc: Y.Doc) => { destroy: () => void; whenSynced: Promise<unknown> };
}

const realFactories: SyncFactories = {
  makeProvider: (cfg) => {
    const provider = new HocuspocusProvider({
      url: cfg.url,
      name: cfg.name,
      token: cfg.token,
      document: cfg.document,
      // v3 onSynced receives { state: boolean }; we ignore the payload
      onSynced: (_data) => cfg.onSynced(),
    });
    // Hocuspocus has no public status getter, so we track it ourselves and
    // replay the last-seen value to late subscribers.
    let current: SyncStatus = 'connecting';
    const subs = new Set<(s: SyncStatus) => void>();
    const handler = (data: { status: SyncStatus }) => {
      current = data.status;
      for (const cb of subs) cb(current);
    };
    provider.on('status', handler);
    return {
      destroy: () => {
        provider.off('status', handler);
        provider.destroy();
      },
      awareness: provider.awareness,
      onStatus: (cb) => {
        subs.add(cb);
        cb(current); // replay current status to the new subscriber
        return () => { subs.delete(cb); };
      },
    };
  },
  makeIndexeddb: (room, doc) => new IndexeddbPersistence(`codeframe.${room}`, doc),
};

/** Attach a Hocuspocus provider + y-indexeddb persistence to `ydoc`. */
export function attachSync(ydoc: Y.Doc, opts: SyncOpts, factories: SyncFactories = realFactories): SyncHandle {
  const idb = factories.makeIndexeddb(opts.room, ydoc);
  let resolveRemote!: () => void;
  const remote = new Promise<void>((r) => { resolveRemote = r; });
  const provider = factories.makeProvider({
    url: opts.wsUrl, name: opts.room, token: opts.token, document: ydoc, onSynced: () => resolveRemote(),
  });
  return {
    whenSynced: Promise.all([idb.whenSynced, remote]).then(() => undefined),
    destroy: () => { provider.destroy(); idb.destroy(); },
    awareness: provider.awareness,
    onStatus: provider.onStatus,
  };
}
